// import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton } from "@mui/material";
import { StyleSheet, css } from "aphrodite";
import { ReactNode, useEffect, useState } from "react";
import { useDrag } from "react-dnd";
import { DraggableItemTypes } from "./DraggableItemTypes";
import * as auto_pi from "@crochik/auto-pi";

import { useForm } from "../../useForm";

interface Props  { // extends FormCellProps
    autoFocus?: boolean;
    field: auto_pi.Field;
    form: auto_pi.Form;
    index: number;

    row: number;
    hidden?: boolean;
    onShowInfo?(field: auto_pi.Field): void;
    onRender?(field: auto_pi.Field): ReactNode;
}

export interface DraggableFormCellItem {
    currentFieldId: number;
    currentRowId: number;
    field: auto_pi.Field;
    hidden?: boolean;
    newFieldId?: number;
    newRowId?: number;
}

interface CollectedProps {
    isDragging: boolean;
}

export function DraggableFormCell(props: Props) {
    const { field, hidden, index, row, onShowInfo, onRender } = props;
    const form = useForm();
    const [hovering, setHovering] = useState(false);

    const [{ isDragging }, drag] = useDrag<DraggableFormCellItem, any, CollectedProps>(
        () => ({
            type: DraggableItemTypes.FIELD,
            item: { currentRowId: row, currentFieldId: index, field, hidden },
            previewOptions: {},
            collect: (monitor) => ({
                isDragging: !!monitor.isDragging(),
            }),
        }),
        [row, index, field]
    );

    useEffect(() => {
        if (isDragging) {
            setHovering(false);
        }
    }, [isDragging]);

    const onMouseEnter = () => setHovering(true);

    const onMouseLeave = () => setHovering(false);

    const onClickInfo = () => onShowInfo?.(field);


    return (
        <div ref={drag} className={css(styles.container, isDragging && styles.dragging)} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <div className={css(styles.draggable)}>
                {
                    onRender?.(field)
                }
                {hovering && onShowInfo && (
                    <div className={css(styles.actions)}>
                        <IconButton size="large" style={{ padding: 8 }} onClick={onClickInfo}>
                            <EditIcon htmlColor="#42A5F5" />
                        </IconButton>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = StyleSheet.create({
    container: {
        position: "relative",
        display: "flex",
        flex: 1,
        borderWidth: 2,
        borderStyle: "solid",
        borderColor: "transparent",
        borderRadius: 6,
        paddingLeft: 8,
        paddingRight: 8,
        overflow: "auto",
        ":hover": {
            borderColor: "#42A5F5",
            boxShadow: "2px 2px 5px 2px rgba(173,173,173,1)",
        },
    },
    emptyField: {
        fontSize: 16,
        border: "1px solid #eeeeee",
        color: "gray",
        marginTop: 12,
        marginBottom: 4,
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 12,
        width: "100%",
    },
    emptyLabel: {},
    draggable: {
        cursor: "move",
        display: "flex",
        flex: 1,
    },
    dragging: {
        opacity: 0.5,
    },
    actions: {
        position: "absolute",
        top: 4,
        right: 8,
        borderRadius: 8,
    },
    actionButton: {
        backgroundColor: "transparent",
        border: "none",
        padding: 0,
        margin: 0,
        cursor: "pointer",
    },
    noClickContainer: {
        pointerEvents: "none",
        width: "100%",
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    fieldTypeIcon: {
        width: '48px',
        height: 'calc(100% - 16px)',
        backgroundColor: 'rgb(240, 240, 240)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 12,
        marginBottom: 4,
        boxSizing: 'border-box',
    },
});
